import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { MapPin, DollarSign, ArrowRight, Briefcase } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { ApplicationModal } from "@/components/ApplicationModal";

export const LatestOpportunities = ({ onViewAll }) => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [applyJob, setApplyJob] = useState(null);

  useEffect(() => {
    api
      .get("/public/jobs/latest")
      .then((r) => setJobs(r.data))
      .catch(() => setJobs([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <section
      id="latest-opportunities"
      data-testid="latest-opportunities-section"
      className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-14 md:py-16 border-t border-white/5"
    >
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 mb-10">
        <div>
          <span className="text-xs font-mono uppercase tracking-[0.2em] text-[#65FFB6]">LIVE OPENINGS</span>
          <h2 className="text-3xl md:text-5xl font-outfit font-bold tracking-tight text-white mt-3">
            Latest Opportunities
          </h2>
          <p className="text-slate-400 font-manrope text-base max-w-2xl mt-4">
            The newest mission-critical roles published across our global infrastructure network.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-16 font-mono text-slate-500 text-sm animate-pulse">LOADING OPENINGS...</div>
      ) : jobs.length === 0 ? (
        <div className="glass-panel rounded-3xl border border-white/10 p-12 text-center text-slate-400 font-manrope">
          No published openings yet. Check back soon.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6" data-testid="latest-jobs-grid">
          {jobs.map((job) => (
            <div
              key={job.id}
              data-testid={`latest-job-${job.id}`}
              className="glass-panel p-6 rounded-3xl border border-white/10 bg-black/40 flex flex-col justify-between hover:border-[#65FFB6]/30 hover:shadow-[0_0_22px_rgba(101,255,182,0.08)] hover:-translate-y-1 transition-all duration-300 group"
            >
              <div>
                <div className="flex items-center justify-between mb-4">
                  <span className="text-[10px] font-mono font-semibold px-2.5 py-1 rounded-full bg-[#0B1F1A] text-[#65FFB6] border border-emerald-500/25">
                    {job.industry}
                  </span>
                  {job.featured && (
                    <span className="text-[9px] font-mono font-bold text-amber-300/90 uppercase">★ Featured</span>
                  )}
                </div>
                <h3 className="text-lg font-outfit font-bold text-white group-hover:text-[#65FFB6] transition-colors leading-snug">
                  <Link data-testid={`latest-job-link-${job.id}`} to={`/jobs/${job.slug}`} className="hover:underline decoration-[#65FFB6]/50 underline-offset-4">{job.title}</Link>
                </h3>
                <div className="flex flex-col gap-1.5 mt-3 text-xs font-mono text-slate-400">
                  <span className="flex items-center gap-1.5"><MapPin className="w-3 h-3 text-slate-500" />{job.location || job.country}</span>
                  <span className="flex items-center gap-1.5"><Briefcase className="w-3 h-3 text-slate-500" />{job.employment_type}</span>
                  <span className="flex items-center gap-1.5"><DollarSign className="w-3 h-3 text-[#65FFB6]" />{job.salary || "Competitive"}</span>
                </div>
                <p className="text-sm font-manrope text-slate-400 mt-4 leading-relaxed line-clamp-3">{job.description}</p>
              </div>
              <Button
                data-testid={`latest-apply-${job.id}`}
                onClick={() => setApplyJob(job)}
                className="mt-6 w-full bg-white/5 hover:bg-[#65FFB6] text-white hover:text-black border border-white/10 hover:border-[#65FFB6] rounded-xl font-outfit font-semibold text-xs h-10 transition-all duration-300"
              >
                Apply Now
              </Button>
            </div>
          ))}
        </div>
      )}

      <div className="flex justify-center mt-12">
        <Button
          data-testid="view-all-opportunities-btn"
          onClick={onViewAll}
          className="bg-[#65FFB6] hover:bg-[#A4FFD6] text-[#05080A] rounded-full font-outfit font-semibold px-8 h-12 text-sm tracking-wide shadow-[0_0_20px_rgba(101,255,182,0.35)] hover:scale-[1.03] transition-all duration-300 flex items-center gap-2"
        >
          View All Opportunities <ArrowRight className="w-4 h-4" />
        </Button>
      </div>

      <ApplicationModal job={applyJob} open={!!applyJob} onClose={() => setApplyJob(null)} />
    </section>
  );
};
